/* Copyright (c) 2020-present CloudIO Inc */

import { BaseProps, Form, RichTextEditor } from 'componentregistry';
import {
  useAttributeDirty,
  useCurrentDataStore,
  useIsEditable,
  useJSONAttribute,
  useStoreRecords,
  useStringAttribute,
} from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { FieldWrapper } from 'components/TextField/BaseField';
import { useStringProperty } from 'platform/PlatformHooks';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { Descendant, Text, Element } from 'slate';
import escapeHtml from 'escape-html';
import { GenericDataRecord } from '@cloudio-saas/datasource-types';
import { parseOptionValues } from 'components/Select/SelectUtils';
import SlateRTE from './Slate';
import type { Option } from './SlateTypes';
import { useErrorMessage } from 'store/useAppStore';
import { useStringExpression } from 'expression/useExpression';
import useTrigger from 'triggers/useTrigger';

export type TextInputType = 'text' | 'password';
const EMPTY_ARRAY: string[] = [];

interface RichTextEditorProps
  extends Required<RichTextEditor.RichTextEditor>,
    BaseProps<'RichTextEditor'> {}

export interface Props extends RichTextEditorProps, Form.HorizontalFormProps {}

const serializeHtml = (node: Element | Text): string => {
  if (Text.isText(node)) {
    let string = escapeHtml(node.text);
    if (node.bold) {
      string = `<strong>${string}</strong>`;
    }
    if (node.italic) {
      string = `<i>${string}</i>`;
    }
    if (node.underline) {
      string = `<u>${string}</u>`;
    }
    if (node.strikethrough) {
      string = `<del>${string}</del>`;
    }
    if (node.code) {
      string = `<code>${string}</code>`;
    }
    return string;
  }

  const children = node.children.map((n) => serializeHtml(n)).join('');
  switch (node.type) {
    case 'quote':
      return `<blockquote><p>${children}</p></blockquote>`;
    case 'p':
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'ol':
    case 'ul':
    case 'li':
      return `<${node.type}>${children}</${node.type}>`;
    case 'a':
      return `<a href="${escapeHtml(node.url)}">${children}</a>`;
    case 'img':
      return `<img src="${escapeHtml(node.url)}" style="max-width:100%" />`;
    case 'video':
      return `<div style="position:relative;padding:75% 0 0 0;">
<iframe title="video" src="${escapeHtml(
        node.url,
      )}?title=0&byline=0&portrait=0" frameBorder="0" 
style="position:absolute;top:0;left:0;width:100%;height:100%"/>
</div>`;
    case 'mention':
      return node.option ? `{{${node.option.value}}}` : '';
    default:
      return children;
  }
};

function toHtml(value: Descendant[]): string {
  if (!value || value.length === 0) return '';
  const children = value.map((n) => serializeHtml(n)).join('');
  return `<div class="tmpl-body">${children}</div>`;
}

const serializeText = (node: Element | Text): string => {
  if (Text.isText(node)) {
    return node.text;
  }

  const children = node.children.map((n) => serializeText(n)).join('');
  switch (node.type) {
    case 'quote':
      return `> ${children}\n\n`;
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
      return `${children}\n\n`;
    case 'p':
      return `${children}\n\n`;
    case 'ol':
    case 'ul':
      return `${children}\n`;
    case 'li':
      return ` - ${children}\n`;
    case 'a':
      return `[${children}](${node.url})`;
    case 'img':
      return `[Image: ${node.url}]`;
    case 'video':
      return `[Video: ${node.url}]`;
    case 'mention':
      return node.option ? `{{${node.option.value}}}` : '';
    default:
      return children;
  }
};

export function toText(value: Descendant[]): string {
  if (!value || value.length === 0) return '';
  const children = value.map((n) => serializeText(n)).join('');
  return children;
}

function IORichTextEditor(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const {
    appUid,
    border,
    children,
    className,
    domProps,
    editorHeight,
    flex,
    formHorizontal,
    height,
    insertable,
    itemId,
    label,
    labelAlignment,
    labelColor,
    labelWidth,
    pageId,
    placeholder,
    required,
    updatable,
    width,
    roleUid,
    htmlAttribute,
    textAttribute,
    mentionDatasourceAlias,
    optionValues,
    valueAttribute,
    labelAttribute,
    platform,
    toolbarLayout,
  } = props;
  const viewAttribute = useStringProperty({
    appUid,
    itemId,
    pageId,
    prop: 'viewAttribute',
  });
  const [value, setValue] = useJSONAttribute(viewAttribute);
  const [, setHtml] = useStringAttribute(htmlAttribute);
  const [, setText] = useStringAttribute(textAttribute);
  const focusedValue = useRef(value);
  const currentValue = useRef(value);

  useEffect(() => {
    currentValue.current = value;
  }, [value]);
  const [onChangeTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnChange',
  );
  const [onValueChangeTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnValueChange',
  );
  const labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;

  const dirty = useAttributeDirty(viewAttribute);
  const disabled = !useIsEditable({
    pageId,
    appUid,
    itemId,
    insertable,
    updatable,
  });
  const store = useCurrentDataStore();

  const errorMessage = useErrorMessage(store, viewAttribute);
  const optionsStore = mentionDatasourceAlias
    ? platform.getStore<GenericDataRecord>(
        appUid,
        pageId,
        mentionDatasourceAlias,
      )
    : null;

  const labelColorString =
    useStringExpression({
      exp: labelColor,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'labelColor',
        data: {},
      },
    }) || undefined;

  const attributeList = useMemo(() => {
    if (!(optionsStore && valueAttribute)) {
      return EMPTY_ARRAY;
    }
    const attList = [valueAttribute];
    if (labelAttribute && labelAttribute !== valueAttribute) {
      attList.push(labelAttribute);
    }
    return attList;
  }, [labelAttribute, optionsStore, valueAttribute]);

  useEffect(() => {
    if (
      optionsStore &&
      attributeList.length /* && !store.hasInitialQueryFired() */
    ) {
      optionsStore.query({
        props: {
          selectAttributeCodeList: attributeList,
          fetchDistinct: true,
        },
      });
    }
  }, [attributeList, optionsStore]);

  const records = useStoreRecords(optionsStore);
  const options: Option[] = useMemo(() => {
    if (optionsStore && records && valueAttribute) {
      return records.map((r) => ({
        value: r[valueAttribute] as string,
        label: labelAttribute ? (r[labelAttribute] as string) : undefined,
      }));
    }
    if (optionValues) {
      const _optionValues = platform.evalIfExpressionString(
        appUid,
        pageId,
        itemId,
        'optionValues',
        {},
        optionValues,
      );
      return (
        _optionValues ? parseOptionValues(_optionValues) : []
      ) as Option[];
      // return parseOptionValues(optionValues) as Option[];
    }
    return [];
  }, [
    appUid,
    itemId,
    labelAttribute,
    optionValues,
    optionsStore,
    pageId,
    platform,
    records,
    valueAttribute,
  ]);

  const val = useMemo(
    () =>
      typeof value === 'string'
        ? JSON.parse(value)
        : Array.isArray(value)
        ? value
        : [{ type: 'p', children: [{ text: '' }] }],
    [value],
  ) as Descendant[];

  const html = useDeferredValue<Descendant[]>(val ?? []);
  const id = useId();

  const handleChange = useCallback(
    async (_value: Descendant[]) => {
      setValue(_value);
      if (htmlAttribute) {
        setHtml(toHtml(html));
      }
      if (textAttribute) {
        setText(toText(html).trim());
      }
      await onChangeTrigger?.();
    },
    [
      setValue,
      htmlAttribute,
      textAttribute,
      onChangeTrigger,
      setHtml,
      html,
      setText,
    ],
  );
  const handleBlur = useCallback(
    async (e: any) => {
      // prevent slate reacting to this click
      e.preventDefault();
      e.stopPropagation();
      if (focusedValue.current !== currentValue.current) {
        await onValueChangeTrigger?.();
      }
    },
    [onValueChangeTrigger],
  );

  const handleFocus = async () => {
    focusedValue.current = value;
  };

  const control = (
    <SlateRTE
      id={id}
      border={border}
      width={width}
      height={height || editorHeight}
      placeholder={placeholder}
      value={val}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      readOnly={disabled || !viewAttribute}
      options={options}
      staticToolbar={toolbarLayout === 'static'}
    />
  );

  return (
    <FieldWrapper
      className={className}
      dirty={dirty}
      domProps={domProps}
      flex={flex}
      ref={ref}
    >
      {formHorizontal && label ? (
        <HorizontalFormField
          control={control}
          label={labelResult}
          labelAlignment={labelAlignment}
          labelWidth={labelWidth}
          error={!!errorMessage}
          required={required}
          labelColor={labelColorString}
          id={id}
        />
      ) : (
        control
      )}
      {children}
    </FieldWrapper>
  );
}

export default React.forwardRef(IORichTextEditor);
